import { Box, CircularProgress, Stack } from '@mui/material';
import React, { Dispatch, SetStateAction, useRef, useState } from 'react';
import { CHAT_WIDGET_PUBLIC_KEY } from '../../../config/appConfig';
import { getChatAgentHostUrl } from '../../../constants/chatAgentConstants';
import { useTranslation } from '../../../hooks/useTranslation';
import { KnowledgeFileData } from '../../../interfaces/Knowledge';
import Text from '../../shared/Text/Text';
import WorkflowStepLabel from '../../shared/WorkflowStepLabel/WorkflowStepLabel';
import {
  AssistantAgentType,
  buildAgentCreationChatUrl,
  getAgentCreationAssistantAppId,
} from './agentCreationAssistantConfig';
import useAssistantFileAttachments from './useAssistantFileAttachments';

const PANEL_HEIGHT = 560;

interface AgentCreationAssistantStepProps {
  agentType: AssistantAgentType;
  label?: string;
  stepLabel?: string;
  description?: string;
  // From useKnowledgeFile: files attached in the assistant join the ones
  // added in the Knowledge step.
  knowledgeFilesData: KnowledgeFileData[];
  setKnowledgeFilesData: Dispatch<SetStateAction<KnowledgeFileData[]>>;
}

// CREATE-only first step: the Elora Chat assistant embedded as an in-app
// panel, where the user describes the agent they want.
const AgentCreationAssistantStep: React.FC<AgentCreationAssistantStepProps> = ({
  agentType,
  label,
  stepLabel,
  description,
  knowledgeFilesData,
  setKnowledgeFilesData,
}) => {
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const host = getChatAgentHostUrl();

  useAssistantFileAttachments({
    iframeRef,
    expectedOrigin: new URL(host).origin,
    agentType,
    knowledgeFilesData,
    setKnowledgeFilesData,
  });

  const chatUrl = buildAgentCreationChatUrl({
    host,
    appId: getAgentCreationAssistantAppId(agentType),
    publicKey: CHAT_WIDGET_PUBLIC_KEY,
    parentOrigin: window.location.origin,
    agentType,
  });

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <WorkflowStepLabel
        stepLabel={stepLabel}
        label={label ?? t('Describe your agent')}
        description={description}
      />
      {chatUrl ? (
        <Box sx={{ position: 'relative', height: PANEL_HEIGHT }}>
          {!isLoaded && (
            <Stack
              spacing={1}
              alignItems="center"
              justifyContent="center"
              sx={{ position: 'absolute', inset: 0 }}
            >
              <CircularProgress size={24} />
              <Text type="body2">{t('Loading the assistant...')}</Text>
            </Stack>
          )}
          <Box
            component="iframe"
            ref={iframeRef}
            title={t('Agent creation assistant')}
            src={chatUrl}
            onLoad={() => setIsLoaded(true)}
            sx={{
              width: '100%',
              height: '100%',
              border: 0,
              visibility: isLoaded ? 'visible' : 'hidden',
            }}
          />
        </Box>
      ) : (
        <Text type="body2">
          {t(
            "The assistant isn't available right now. Press Next to set up your agent yourself."
          )}
        </Text>
      )}
    </Stack>
  );
};

export default AgentCreationAssistantStep;
